import { useEffect, useRef, useState } from 'react';
import { extractErrorMessage } from '../utils/fileStorageErrors';
import {
  isUploadCancelled,
  uploadFileInChunks,
  type FileManagerPathInfo,
} from '../services/fileUploadService';

export type UploadStatus = 'queued' | 'uploading' | 'done' | 'failed' | 'cancelled';

export type UploadEntry = {
  id: string;
  file: File;
  parentId?: string;
  destinationPathInfo: FileManagerPathInfo[];
  uploadedBytes: number;
  percent: number;
  status: UploadStatus;
  isProcessing: boolean;
  error?: string;
  startedAt: number;
  completedAt?: number;
};

type UseFileUploadsOptions = {
  onComplete: () => void;
  onError: (message: string) => void;
};

const TRANSFER_PROGRESS_LIMIT = 70;
const PROCESSING_PROGRESS_LIMIT = 99;
const PROCESSING_PROGRESS_DURATION = 15000;

export const useFileUploads = ({ onComplete, onError }: UseFileUploadsOptions) => {
  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const controllersRef = useRef<Map<string, AbortController>>(new Map());

  const updateEntry = (id: string, update: Partial<UploadEntry>) => {
    setEntries((current) =>
      current.map((entry) => (entry.id === id ? { ...entry, ...update } : entry))
    );
  };

  const updateProcessingProgress = (id: string) => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const progress = Math.min(
        PROCESSING_PROGRESS_LIMIT,
        TRANSFER_PROGRESS_LIMIT +
          Math.round(
            (elapsed / PROCESSING_PROGRESS_DURATION) *
              (PROCESSING_PROGRESS_LIMIT - TRANSFER_PROGRESS_LIMIT)
          )
      );
      updateEntry(id, { percent: progress, isProcessing: true });
      if (progress >= PROCESSING_PROGRESS_LIMIT) {
        window.clearInterval(timer);
      }
    }, 250);
    return timer;
  };

  const startUpload = async (entry: UploadEntry) => {
    const controller = new AbortController();
    controllersRef.current.set(entry.id, controller);
    let processingTimer: number | undefined;
    updateEntry(entry.id, {
      status: 'uploading',
      isProcessing: false,
      startedAt: Date.now(),
      error: undefined,
    });

    try {
      await uploadFileInChunks(
        entry.file,
        entry.destinationPathInfo,
        controller.signal,
        ({ uploadedBytes, finalChunkStarted }) => {
          if (finalChunkStarted && processingTimer === undefined) {
            processingTimer = updateProcessingProgress(entry.id);
          }
          updateEntry(entry.id, {
            uploadedBytes,
            percent: Math.min(
              TRANSFER_PROGRESS_LIMIT,
              Math.round((uploadedBytes / entry.file.size) * TRANSFER_PROGRESS_LIMIT)
            ),
          });
        }
      );

      if (processingTimer !== undefined) {
        window.clearInterval(processingTimer);
      }
      updateEntry(entry.id, {
        status: 'done',
        isProcessing: false,
        uploadedBytes: entry.file.size,
        percent: 100,
        completedAt: Date.now(),
      });
      onComplete();
    } catch (error) {
      if (processingTimer !== undefined) {
        window.clearInterval(processingTimer);
      }
      if (isUploadCancelled(error, controller.signal)) {
        updateEntry(entry.id, {
          status: 'cancelled',
          isProcessing: false,
          completedAt: Date.now(),
        });
      } else {
        const message = extractErrorMessage(error, 'Failed to upload file.');
        updateEntry(entry.id, {
          status: 'failed',
          isProcessing: false,
          error: message,
          completedAt: Date.now(),
        });
        onError(message);
      }
    } finally {
      controllersRef.current.delete(entry.id);
    }
  };

  const uploadFiles = async (
    files: File[],
    parentId: string | undefined,
    destinationPathInfo: FileManagerPathInfo[]
  ) => {
    const newEntries = files.map<UploadEntry>((file) => ({
      id: crypto.randomUUID(),
      file,
      parentId,
      destinationPathInfo,
      uploadedBytes: 0,
      percent: 0,
      status: 'queued',
      isProcessing: false,
      startedAt: Date.now(),
    }));
    setEntries((current) => [...current, ...newEntries]);
    await Promise.all(newEntries.map((entry) => startUpload(entry)));
  };

  const cancelUpload = (id: string) => {
    const controller = controllersRef.current.get(id);
    if (controller) {
      controller.abort();
    } else {
      updateEntry(id, { status: 'cancelled', completedAt: Date.now() });
    }
  };

  const retryUpload = (id: string) => {
    const entry = entries.find((current) => current.id === id);
    if (!entry) return;
    const retryEntry = {
      ...entry,
      status: 'queued' as const,
      uploadedBytes: 0,
      percent: 0,
      isProcessing: false,
      error: undefined,
    };
    updateEntry(id, retryEntry);
    void startUpload(retryEntry);
  };

  useEffect(() => {
    const controllers = controllersRef.current;
    return () => {
      controllers.forEach((controller) => controller.abort());
    };
  }, []);

  return {
    entries,
    uploadFiles,
    cancelUpload,
    retryUpload,
  };
};
